// No i18n JSX runtime here: this renders written (AI) content, never
// translated; the few interface texts go through tr.
import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cx } from '../../theme/cx';
import { tr } from '../../i18n';

// One markdown renderer for every AI text. Variants only change the scale and
// the heading levels:
// - report: stored daily/expert reports, headings shifted one level down
//   (the page owns the h1, report titles are h2);
// - analysis: on-demand analyses, levels unchanged;
// - rumor: denser cards; chat: compact bubbles.
// Links are kept only for http(s) and in-page anchors (opened in a new tab
// when external) and images are never loaded. Raw HTML stays escaped; pass
// skipHtml to drop it entirely.
const SCALES = {
  report: {
    text: 'text-[15px] leading-[1.7]',
    block: 'my-4',
    h1: 'mb-4 mt-10 font-display text-display-md font-normal text-fg',
    h2: 'mb-3 mt-10 border-b border-line pb-2 font-display text-[22px] font-normal leading-8 text-gold-100',
    h3: 'mb-2 mt-7 text-[17px] font-semibold text-fg',
    h4: 'mb-2 mt-6 text-[15px] font-semibold text-fg-body',
  },
  rumor: {
    text: 'text-sm leading-relaxed',
    block: 'my-3',
    h1: 'mb-3 mt-6 font-display text-xl font-normal text-fg',
    h2: 'mb-3 mt-7 border-b border-line pb-2 font-display text-lg font-normal text-fg',
    h3: 'mb-2 mt-5 text-[15px] font-semibold text-gold-200',
    h4: 'mb-2 mt-4 text-sm font-semibold text-fg-body',
  },
  chat: {
    text: 'text-[13.5px] leading-relaxed',
    block: 'my-2',
    h1: 'mb-1.5 mt-3 font-display text-base font-normal text-fg',
    h2: 'mb-1.5 mt-3 font-display text-[15px] font-normal text-fg',
    h3: 'mb-1 mt-2 text-[13.5px] font-semibold text-fg',
    h4: 'mb-1 mt-2 text-[13.5px] font-semibold text-fg-body',
  },
};
SCALES.analysis = SCALES.report;

const SHIFTED = { h1: 'h2', h2: 'h3', h3: 'h4', h4: 'h5', h5: 'h6', h6: 'h6' };

const LINK = 'break-words font-medium text-gold-300 underline decoration-gold-500/40 underline-offset-2 transition-colors hover:text-gold-200 hover:decoration-gold-300';

const SafeLink = ({ href, children }) => {
  if (/^https?:\/\//i.test(href || '')) return <a href={href} target="_blank" rel="noopener noreferrer" className={LINK}>{children}</a>;
  if (href?.startsWith('#')) return <a href={href} className={LINK}>{children}</a>;
  return <span>{children}</span>;
};

const build = (variant) => {
  const s = SCALES[variant];
  const heading = (level, styleKey) => ({ children }) => {
    const Tag = variant === 'report' ? SHIFTED[level] : level;
    return <Tag className={cx(s[styleKey], 'first:mt-0')}>{children}</Tag>;
  };
  return {
    h1: heading('h1', 'h1'),
    h2: heading('h2', 'h2'),
    h3: heading('h3', 'h3'),
    h4: heading('h4', 'h4'),
    h5: heading('h5', 'h4'),
    h6: heading('h6', 'h4'),
    p: ({ children }) => <p className={cx(s.block, s.text, 'text-fg-body first:mt-0 last:mb-0')}>{children}</p>,
    ul: ({ children }) => <ul className={cx(s.block, 'list-disc space-y-1.5 pl-5 marker:text-gold-500')}>{children}</ul>,
    ol: ({ children }) => <ol className={cx(s.block, 'list-decimal space-y-1.5 pl-5 marker:text-gold-400')}>{children}</ol>,
    li: ({ children }) => <li className={cx(s.text, 'pl-1 text-fg-body [&>p]:my-1')}>{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-fg">{children}</strong>,
    em: ({ children }) => <em className="italic text-fg">{children}</em>,
    blockquote: ({ children }) => (
      <blockquote className={cx(s.block, 'rounded-r-control border-l-2 border-gold-500 bg-surface-2 py-2.5 pl-4 pr-3 [&>p]:my-1')}>{children}</blockquote>
    ),
    // Focusable so a table wider than the column can be scrolled by keyboard.
    table: ({ children }) => (
      <div role="region" aria-label={tr('Tableau')} tabIndex={0} className={cx(s.block, 'overflow-x-auto rounded-control border border-line')}>
        <table className="w-full border-collapse text-left text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-surface-2">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-line [&>tr:nth-child(even)]:bg-surface-2/40">{children}</tbody>,
    // Cells keep whole words (the frame scrolls) instead of inheriting the
    // prose's overflow-wrap:anywhere, which would squeeze every column.
    th: ({ children, style }) => <th style={style} className="whitespace-nowrap px-3 py-2.5 text-eyebrow font-semibold uppercase text-fg-subtle">{children}</th>,
    td: ({ children, style }) => <td style={style} className="min-w-[7rem] px-3 py-2.5 align-top text-fg-body [overflow-wrap:normal] first:min-w-0">{children}</td>,
    a: SafeLink,
    img: () => null,
    hr: () => <hr className="my-8 border-line" />,
    pre: ({ children }) => (
      <pre className={cx(s.block, 'overflow-x-auto rounded-control border border-line bg-surface-3 p-4 text-[13px] leading-relaxed text-fg-body [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-inherit')}>{children}</pre>
    ),
    code: ({ children }) => <code className="rounded bg-surface-3 px-1.5 py-0.5 font-mono text-[0.85em] text-gold-200">{children}</code>,
  };
};

export const proseComponents = Object.fromEntries(Object.keys(SCALES).map((variant) => [variant, build(variant)]));

const PLUGINS = [remarkGfm];

export const MarkdownProse = memo(({ children, variant = 'report', skipHtml = false, className }) => (
  <div className={cx('min-w-0 [overflow-wrap:anywhere]', className)}>
    <ReactMarkdown remarkPlugins={PLUGINS} components={proseComponents[variant] || proseComponents.report} skipHtml={skipHtml}>
      {children || ''}
    </ReactMarkdown>
  </div>
));
